import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  FiVideo,
  FiArrowLeft,
  FiBook,
  FiLayers,
  FiPackage,
  FiCalendar,
  FiZap,
  FiInfo,
  FiCheckCircle,
  FiXCircle,
  FiUnlock,
  FiLock,
  FiUser,
} from "react-icons/fi";
import Button from "../../components/Button/Button";
import adminApi from "../../api/adminApi";
import FormInput from "../../components/FormInput/FormInput";
import Select from "../../components/Select/Select";
import "./CreateLecture.css";

const CreateLecture = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batchIdFromUrl = searchParams.get("batchId");
  const moduleIdFromUrl = searchParams.get("moduleId");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [openingModule, setOpeningModule] = useState(false);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [modules, setModules] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [instructorType, setInstructorType] = useState("self"); // "self" or "instructor"
  const [formData, setFormData] = useState({
    batch: batchIdFromUrl || "",
    module: moduleIdFromUrl || "",
    teacher: "",
    topic: "",
    description: "",
    startTime: "",
    endTime: "",
    meetingLink: "",
    recordingLink: "",
    createFermion: true, // Auto-create Google Meet link by default
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (batchIdFromUrl) {
      loadBatch(batchIdFromUrl);
    }
  }, [batchIdFromUrl]);

  useEffect(() => {
    if (formData.batch) {
      loadModulesForBatch(formData.batch);
      if (moduleIdFromUrl) {
        setFormData((prev) => ({ ...prev, module: moduleIdFromUrl }));
      }
    }
  }, [formData.batch, moduleIdFromUrl]);

  // Update selected module when modules list changes
  useEffect(() => {
    if (formData.module && modules.length > 0) {
      const module = modules.find((m) => m._id === formData.module);
      if (module) {
        setSelectedModule(module);
      }
    }
  }, [modules, formData.module]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load current user from localStorage
      const userStr = localStorage.getItem("user");
      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          setCurrentUser(user);
          // If current user is admin/superadmin, set teacher to self by default
          if (user && ["admin", "superadmin"].includes(user.role)) {
            setFormData((prev) => ({ ...prev, teacher: user._id }));
          }
        } catch (err) {
          console.error("Failed to parse user data:", err);
        }
      }
      await Promise.all([loadCourses(), loadBatches(), loadTeachers()]);
      if (batchIdFromUrl) {
        await loadBatch(batchIdFromUrl);
      }
    } catch (err) {
      setError("Failed to load initial data");
    } finally {
      setLoading(false);
    }
  };

  const loadCourses = async () => {
    try {
      const response = await adminApi.get("/courses", {
        params: { limit: 1000 },
      });
      const coursesList = response.data?.courses || response.data?.data || [];
      setCourses(coursesList);
    } catch (err) {
      console.error("Failed to load courses:", err);
    }
  };

  const loadBatches = async () => {
    try {
      const response = await adminApi.get("/batches", {
        params: { limit: 1000 },
      });
      const batchesList = response.data?.batches || response.data?.data || [];
      setBatches(batchesList);
      if (batchIdFromUrl) {
        const batch = batchesList.find((b) => b._id === batchIdFromUrl);
        if (batch) {
          setSelectedBatch(batch);
          if (batch.course) {
            const courseId =
              typeof batch.course === "object"
                ? batch.course._id
                : batch.course;
            const course = courses.find((c) => c._id === courseId);
            if (course) {
              setSelectedCourse(course);
            }
          }
        }
      }
    } catch (err) {
      console.error("Failed to load batches:", err);
    }
  };

  const loadBatch = async (batchId) => {
    try {
      const response = await adminApi.get(`/batches/${batchId}`);
      const batchData = response.data;
      setSelectedBatch(batchData);
      if (batchData.course) {
        const courseId =
          typeof batchData.course === "object"
            ? batchData.course._id
            : batchData.course;
        try {
          const courseResponse = await adminApi.get(
            `/courses/${courseId}`
          );
          setSelectedCourse(courseResponse.data);
        } catch (err) {
          console.error("Failed to load course:", err);
        }
      }
    } catch (err) {
      console.error("Failed to load batch:", err);
    }
  };

  const loadModulesForBatch = async (batchId) => {
    try {
      const response = await adminApi.get(`/batches/${batchId}/modules`);
      const modulesList =
        response.data?.modules || response.data?.data || response.data || [];
      setModules(modulesList);
      if (moduleIdFromUrl) {
        const module = modulesList.find((m) => m._id === moduleIdFromUrl);
        if (module) {
          setSelectedModule(module);
        }
      }
    } catch (err) {
      console.error("Failed to load modules:", err);
    }
  };

  const loadTeachers = async () => {
    try {
      const response = await adminApi.get("/users", {
        params: { role: "mentor", limit: 1000 },
      });
      const usersList = response.data?.users || response.data?.data || [];
      setTeachers(usersList);
    } catch (err) {
      console.error("Failed to load teachers:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    if (!formData.batch) {
      setError("Please select a batch");
      setSubmitting(false);
      return;
    }

    if (!formData.module || String(formData.module).trim() === "") {
      setError("Please select a module");
      setSubmitting(false);
      return;
    }

    if (selectedModule && !selectedModule.isOpen) {
      setError(
        "Please open the module first. Click 'Open Module Now' button to open it."
      );
      setSubmitting(false);
      return;
    }

    try {
      const lectureData = {
        topic: formData.topic,
        description: formData.description,
        startTime: new Date(formData.startTime).toISOString(),
        endTime: new Date(formData.endTime).toISOString(),
        meetingLink: formData.meetingLink,
        recordingLink: formData.recordingLink,
      };

      // Only include createFermion if it's true
      if (formData.createFermion) {
        lectureData.createFermion = true;
      }

      // Only include module if it's provided and not empty
      if (formData.module && String(formData.module).trim() !== "") {
        lectureData.module = formData.module;
      }

      // Handle teacher assignment based on instructor type
      if (instructorType === "self" && currentUser) {
        // Admin/superadmin taking the lecture themselves
        lectureData.teacher = currentUser._id;
      } else if (
        instructorType === "instructor" &&
        formData.teacher &&
        String(formData.teacher).trim() !== ""
      ) {
        // Assigning to an instructor
        lectureData.teacher = formData.teacher;
      }
      // If neither is set, don't include teacher field (optional)

      const response = await adminApi.post(
        `/batches/${formData.batch}/lectures`,
        lectureData
      );
      navigate(`/lectures/${response.data._id}`);
    } catch (err) {
      setError(err.response?.data?.message || "Failed to create lecture");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBatchChange = (batchId) => {
    const batch = batches.find((b) => b._id === batchId);
    setSelectedBatch(batch || null);
    if (batch && batch.course) {
      const courseId =
        typeof batch.course === "object" ? batch.course._id : batch.course;
      const course = courses.find((c) => c._id === courseId);
      setSelectedCourse(course || null);
    } else {
      setSelectedCourse(null);
    }
    setModules([]);
    setSelectedModule(null);
    setFormData((prev) => ({ ...prev, batch: batchId, module: "" }));
  };

  const handleModuleChange = (moduleId) => {
    const module = modules.find((m) => m._id === moduleId);
    setSelectedModule(module || null);
    setFormData((prev) => ({ ...prev, module: moduleId }));
  };

  const handleOpenModule = async () => {
    if (!selectedModule || !formData.batch) return;
    setOpeningModule(true);
    setError(null);
    try {
      await adminApi.patch(`/modules/${selectedModule._id}/open`);
      // Reload modules to update status
      await loadModulesForBatch(formData.batch);
      // The useEffect hook will automatically update selectedModule when modules change
    } catch (err) {
      setError(err.response?.data?.message || "Failed to open module");
    } finally {
      setOpeningModule(false);
    }
  };

  if (loading) {
    return <div className="create-loading">Loading...</div>;
  }

  return (
    <div className="create-lecture-page">
      <div className="page-header">
        <div>
          <Button
            variant="secondary"
            size="small"
            onClick={() => navigate("/lectures")}
          >
            <FiArrowLeft size={16} />
            Back to Lectures
          </Button>
          <h1>
            <FiVideo size={24} />
            Schedule New Lecture
          </h1>
          <p>Create a new lecture for a batch</p>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <div className="form-container">
        <form onSubmit={handleSubmit} className="lecture-form">
          <div className="form-section">
            <h2>Batch & Module Selection</h2>
            <Select
              label="Batch"
              name="batch"
              value={formData.batch}
              onChange={(e) => handleBatchChange(e.target.value)}
              options={[
                { value: "", label: "Select a batch" },
                ...batches.map((b) => ({
                  value: b._id,
                  label: `${b.batchName} (${
                    b.course && typeof b.course === "object" && b.course.title
                      ? b.course.title
                      : "Course"
                  })`,
                })),
              ]}
              required
            />

            {selectedBatch && (
              <div className="info-card">
                <h3>
                  <FiLayers size={18} />
                  Batch Information
                </h3>
                <p>
                  <strong>Batch:</strong> {selectedBatch.batchName}
                </p>
                {selectedCourse && (
                  <p>
                    <strong>Course:</strong>{" "}
                    <Link to={`/courses/${selectedCourse._id}`}>
                      {selectedCourse.title}
                    </Link>
                  </p>
                )}
                <p>
                  <strong>Start Date:</strong>{" "}
                  {selectedBatch.startDate
                    ? new Date(selectedBatch.startDate).toLocaleDateString()
                    : "—"}
                </p>
                <p>
                  <strong>End Date:</strong>{" "}
                  {selectedBatch.endDate
                    ? new Date(selectedBatch.endDate).toLocaleDateString()
                    : "—"}
                </p>
              </div>
            )}

            {formData.batch && (
              <>
                {modules.length > 0 ? (
                  <>
                    <Select
                      label="Module"
                      name="module"
                      value={formData.module}
                      onChange={(e) => handleModuleChange(e.target.value)}
                      options={[
                        { value: "", label: "Select a module" },
                        ...modules.map((m) => ({
                          value: m._id,
                          label: `${m.order}. ${m.title} ${
                            m.isOpen ? "✓ (Open)" : "🔒 (Closed)"
                          }`,
                        })),
                      ]}
                      required
                    />
                    {selectedModule && (
                      <div
                        className={`info-card ${
                          selectedModule.isOpen
                            ? "module-open"
                            : "module-closed"
                        }`}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <h3
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "var(--space-2)",
                                marginBottom: "var(--space-3)",
                              }}
                            >
                              <FiPackage size={18} />
                              Module Information
                              {selectedModule.isOpen ? (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "4px 8px",
                                    background: "rgba(34, 197, 94, 0.1)",
                                    color: "var(--success, #22c55e)",
                                    borderRadius: "var(--radius-full)",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    marginLeft: "auto",
                                  }}
                                >
                                  <FiCheckCircle size={12} />
                                  Open - Ready to Schedule
                                </span>
                              ) : (
                                <span
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    padding: "4px 8px",
                                    background: "rgba(251, 191, 36, 0.1)",
                                    color: "var(--warning, #f59e0b)",
                                    borderRadius: "var(--radius-full)",
                                    fontSize: "0.75rem",
                                    fontWeight: 600,
                                    marginLeft: "auto",
                                  }}
                                >
                                  <FiXCircle size={12} />
                                  Closed
                                </span>
                              )}
                            </h3>
                            <p>
                              <strong>Module:</strong> {selectedModule.title}
                            </p>
                            {selectedModule.description && (
                              <p>
                                <strong>Description:</strong>{" "}
                                {selectedModule.description}
                              </p>
                            )}
                            <p>
                              <strong>Order:</strong> {selectedModule.order}
                            </p>
                            {selectedModule.duration && (
                              <p>
                                <strong>Duration:</strong>{" "}
                                {selectedModule.duration}
                              </p>
                            )}
                            {!selectedModule.isOpen && (
                              <div
                                style={{
                                  marginTop: "var(--space-3)",
                                  padding: "var(--space-3)",
                                  background: "rgba(251, 191, 36, 0.1)",
                                  border: "1px solid rgba(251, 191, 36, 0.3)",
                                  borderRadius: "var(--radius-md)",
                                }}
                              >
                                <strong
                                  style={{ color: "var(--warning, #f59e0b)" }}
                                >
                                  ⚠️ Module is Closed
                                </strong>
                                <p
                                  style={{
                                    margin: "var(--space-2) 0 var(--space-3) 0",
                                    color: "var(--text-secondary)",
                                    fontSize: "0.875rem",
                                  }}
                                >
                                  Students will NOT be able to see this lecture
                                  until the module is opened. Open the module
                                  now to make it available to students.
                                </p>
                                <Button
                                  size="small"
                                  variant="primary"
                                  onClick={handleOpenModule}
                                  loading={openingModule}
                                  style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <FiUnlock size={14} />
                                  Open Module Now
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {!selectedModule && (
                      <div
                        style={{
                          padding: "var(--space-3)",
                          background: "rgba(148, 163, 184, 0.1)",
                          border: "1px solid rgba(148, 163, 184, 0.2)",
                          borderRadius: "var(--radius-md)",
                          marginTop: "var(--space-2)",
                          fontSize: "0.875rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        Please select a module to schedule the lecture. If you
                        don't select a module, students won't be able to see
                        this lecture.
                      </div>
                    )}
                  </>
                ) : (
                  <div
                    style={{
                      padding: "var(--space-4)",
                      background: "rgba(148, 163, 184, 0.1)",
                      border: "1px solid rgba(148, 163, 184, 0.2)",
                      borderRadius: "var(--radius-md)",
                      marginTop: "var(--space-2)",
                      textAlign: "center",
                    }}
                  >
                    <FiPackage
                      size={24}
                      style={{ marginBottom: "var(--space-2)" }}
                    />
                    <p style={{ margin: 0, color: "var(--text-secondary)" }}>
                      No modules available for this batch. Create modules first
                      from the batch detail page.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="form-section">
            <h2>Lecture Details</h2>

            {/* Instructor Type Selection */}
            {currentUser &&
              ["admin", "superadmin"].includes(currentUser.role) && (
                <div style={{ marginBottom: "var(--space-4)" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "var(--space-2)",
                      fontWeight: 500,
                    }}
                  >
                    Instructor Type{" "}
                    <span
                      style={{
                        color: "var(--text-secondary)",
                        fontWeight: "normal",
                      }}
                    >
                      (Who will take this lecture?)
                    </span>
                  </label>
                  <div
                    style={{
                      display: "flex",
                      gap: "var(--space-2)",
                      flexWrap: "wrap",
                    }}
                  >
                    <Button
                      type="button"
                      variant={
                        instructorType === "self" ? "primary" : "secondary"
                      }
                      onClick={() => {
                        setInstructorType("self");
                        if (currentUser) {
                          setFormData((prev) => ({
                            ...prev,
                            teacher: currentUser._id,
                          }));
                        }
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FiUser size={16} />
                      Self (I will take this lecture)
                    </Button>
                    <Button
                      type="button"
                      variant={
                        instructorType === "instructor"
                          ? "primary"
                          : "secondary"
                      }
                      onClick={() => {
                        setInstructorType("instructor");
                        setFormData((prev) => ({ ...prev, teacher: "" }));
                      }}
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FiLayers size={16} />
                      Assign to Instructor
                    </Button>
                  </div>
                  {instructorType === "self" && currentUser && (
                    <p
                      style={{
                        marginTop: "var(--space-2)",
                        fontSize: "0.875rem",
                        color: "var(--text-secondary)",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <FiCheckCircle size={14} />
                      You ({currentUser.firstName} {currentUser.lastName}) will
                      be the instructor for this lecture
                    </p>
                  )}
                </div>
              )}

            {/* Teacher/Instructor Selection - Only show when instructor type is "instructor" or if current user is not admin/superadmin */}
            {(instructorType === "instructor" ||
              !currentUser ||
              !["admin", "superadmin"].includes(currentUser?.role)) && (
              <Select
                label={
                  currentUser &&
                  ["admin", "superadmin"].includes(currentUser.role)
                    ? "Select Instructor"
                    : "Teacher (Optional)"
                }
                name="teacher"
                value={formData.teacher}
                onChange={(e) =>
                  setFormData({ ...formData, teacher: e.target.value })
                }
                options={[
                  {
                    value: "",
                    label:
                      currentUser &&
                      ["admin", "superadmin"].includes(currentUser.role)
                        ? "Select an instructor"
                        : "Select a teacher (optional)",
                  },
                  ...teachers.map((t) => ({
                    value: t._id,
                    label: `${t.firstName} ${t.lastName}`.trim() || t.email,
                  })),
                ]}
              />
            )}
            <FormInput
              label="Topic"
              name="topic"
              value={formData.topic}
              onChange={(e) =>
                setFormData({ ...formData, topic: e.target.value })
              }
              required
            />
            <FormInput
              label="Description"
              name="description"
              type="textarea"
              rows={4}
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
            />
            <div className="form-row">
              <FormInput
                label="Start Time"
                name="startTime"
                type="datetime-local"
                value={formData.startTime}
                onChange={(e) =>
                  setFormData({ ...formData, startTime: e.target.value })
                }
                required
              />
              <FormInput
                label="End Time"
                name="endTime"
                type="datetime-local"
                value={formData.endTime}
                onChange={(e) =>
                  setFormData({ ...formData, endTime: e.target.value })
                }
                required
              />
            </div>

            {/* FermionService Integration */}
            <div className="fermion-section">
              <div className="fermion-toggle-group">
                <label className="fermion-toggle-label">
                  <input
                    type="checkbox"
                    name="createFermion"
                    checked={formData.createFermion}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        createFermion: e.target.checked,
                        meetingLink: e.target.checked
                          ? ""
                          : formData.meetingLink,
                      })
                    }
                    className="fermion-toggle"
                  />
                  <div className="fermion-toggle-content">
                    <div className="fermion-toggle-header">
                      <FiZap size={18} className="fermion-icon" />
                      <span className="fermion-toggle-title">
                        Auto-create Google Meet link (Recommended)
                      </span>
                    </div>
                    <p className="fermion-toggle-description">
                      Automatically create a Google Meet link for this lecture.
                    </p>
                  </div>
                </label>
                {formData.createFermion && (
                  <div className="fermion-info-box">
                    <FiInfo size={16} />
                    <div>
                      <strong>Google Meet Benefits:</strong>
                      <ul>
                        <li>Easy access with Google accounts</li>
                        <li>Automatic cloud recordings</li>
                        <li>Simple screen sharing</li>
                        <li>Built-in chat and transcription</li>
                        <li>No additional software needed</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <FormInput
              label={`Meeting Link ${
                formData.createFermion
                  ? "(Auto-generated for Google Meet)"
                  : "(Optional)"
              }`}
              name="meetingLink"
              value={formData.meetingLink}
              onChange={(e) =>
                setFormData({ ...formData, meetingLink: e.target.value })
              }
              placeholder={
                formData.createFermion
                  ? "Google Meet link will be created automatically"
                  : "https://meet.google.com/... or custom link"
              }
              disabled={formData.createFermion}
            />
            <FormInput
              label="Recording Link (Optional)"
              name="recordingLink"
              value={formData.recordingLink}
              onChange={(e) =>
                setFormData({ ...formData, recordingLink: e.target.value })
              }
              placeholder="https://youtube.com/..."
            />
          </div>

          <div className="form-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/lectures")}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={submitting || openingModule}
            >
              Schedule Lecture
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateLecture;
